import React, { useRef, useState } from 'react';
import {
  AppState, AppStateUpdate,
  PictureQuality, GridDensity, PreviewTexturePx,
  MACHINE_TYPES, PICTURE_QUALITY_PX, MATERIAL_COLORS,
} from '../types';
import FemAdvancedDialog, { FemParams } from './FemAdvancedDialog';

interface Props {
  appState: AppState;
  onSave:   (update: AppStateUpdate) => void;
  onClose:  () => void;
}

const QUALITY_LABELS: Record<PictureQuality, string> = {
  Low:    `Low Picture Quality (${PICTURE_QUALITY_PX.Low} px)`,
  Normal: `Normal Picture Quality (${PICTURE_QUALITY_PX.Normal} px)`,
  High:   `High Picture Quality (${PICTURE_QUALITY_PX.High} px)`,
};

const DENSITY_LABELS: Record<GridDensity, string> = {
  Low:      'Low Grid Density (600)',
  Normal:   'Normal Grid Density (1200)',
  High:     'High Grid Density (2400)',
  VeryHigh: 'Very High G. D. (4800)',
};


const PREVIEW_TEX_OPTIONS: { value: PreviewTexturePx; label: string }[] = [
  { value: 512,  label: 'Preview texture: 512 px (fast)'   },
  { value: 1024, label: 'Preview texture: 1024 px'         },
  { value: 1536, label: 'Preview texture: 1536 px (slower)' },
];

// ── Numeric field helper ──────────────────────────────────────

interface NumFieldProps {
  label: string;
  value: number;
  min?:  number;
  step?: number;
  onChange: (v: number) => void;
}

const NumField: React.FC<NumFieldProps> = ({ label, value, min = 0, step = 0.1, onChange }) => (
  <div className="param-field">
    <label className="param-label">{label}</label>
    <input
      type="number"
      className="param-input"
      value={value}
      min={min}
      step={step}
      onChange={e => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v) && v >= min) onChange(v);
      }}
    />
  </div>
);

// ── Component ─────────────────────────────────────────────────

const ParametersDialog: React.FC<Props> = ({ appState, onSave, onClose }) => {
  const [loc, setLoc] = useState({
    xSizeMm:            appState.materialWidth,
    ySizeMm:            appState.materialHeight,
    marginMm:           appState.marginMm,
    thicknessMm:        appState.thicknessMm,
    opMarginMm:         appState.opMarginMm,
    materialColor:      appState.materialColor,
    transparency:       appState.transparency,
    materialAboveImage: appState.materialAboveImage,
    multipleContours:   appState.multipleContours,
    pictureQuality:     appState.pictureQuality,
    gridDensity:        appState.gridDensity,
    machineType:        appState.machineType,
    previewTexturePx:   appState.previewTexturePx,
  });

  const [femParams, setFemParams] = useState<FemParams>({
    femRampLevels:        appState.femRampLevels,
    femRampCheckPeriod:   appState.femRampCheckPeriod,
    femBendingFactor:     appState.femBendingFactor,
    femBoostFactor:       appState.femBoostFactor,
    femMaterialFailRatio: appState.femMaterialFailRatio,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCustomColor, setShowCustomColor] = useState(false);
  const [customHex, setCustomHex] = useState(appState.materialColor);

  const set = <K extends keyof typeof loc>(k: K, v: (typeof loc)[K]) =>
    setLoc(prev => ({ ...prev, [k]: v }));

  const currentMachine = MACHINE_TYPES.find(m => m.id === loc.machineType) ?? MACHINE_TYPES[0];

  const handleSave = () => {
    onSave({
      materialWidth:      loc.xSizeMm,
      materialHeight:     loc.ySizeMm,
      marginMm:           loc.marginMm,
      thicknessMm:        loc.thicknessMm,
      opMarginMm:         loc.opMarginMm,
      materialColor:      loc.materialColor,
      transparency:       loc.transparency,
      materialAboveImage: loc.materialAboveImage,
      multipleContours:   loc.multipleContours,
      pictureQuality:     loc.pictureQuality,
      gridDensity:        loc.gridDensity,
      machineType:        loc.machineType,
      previewTexturePx:   loc.previewTexturePx,
      ...femParams,
    });
    onClose();
  };

  return (
    <>
    <div className="layers-overlay">
      <div className="param-dialog param-dialog-2col">

        <div className="param-columns">

          {/* ── Kolumna 1: wymiary, materiał ── */}
          <div className="param-col">
            <div className="param-title">Enter values:</div>

            <div className="param-row2">
              <NumField label="XSize [mm]:" value={loc.xSizeMm}
                min={1} step={1} onChange={v => set('xSizeMm', Math.min(v, currentMachine.widthMm))} />
              <NumField label="YSize [mm]:" value={loc.ySizeMm}
                min={1} step={1} onChange={v => set('ySizeMm', Math.min(v, currentMachine.heightMm))} />
            </div>
            <div className="param-hint">
              Max: {currentMachine.widthMm} × {currentMachine.heightMm} mm (machine {currentMachine.id})
            </div>

            <NumField label="Margin [mm]:" value={loc.marginMm}
              min={0} step={0.5} onChange={v => set('marginMm', v)} />

            <div className="param-row2">
              <NumField label="Thickness [mm]:" value={loc.thicknessMm}
                min={0.1} step={0.1} onChange={v => set('thicknessMm', v)} />
              <NumField label="Op. marg. [mm]:" value={loc.opMarginMm}
                min={0} step={0.1} onChange={v => set('opMarginMm', v)} />
            </div>

            <div className="param-field">
              <label className="param-label">Material color:</label>
              <div className="param-color-row">
                <div className="param-color-swatch" style={{ background: loc.materialColor }} />
                <select
                  className="param-select"
                  value={MATERIAL_COLORS.some(c => c.hex === loc.materialColor) ? loc.materialColor : ''}
                  onChange={e => {
                    if (e.target.value === '__custom__') {
                      setCustomHex(loc.materialColor);
                      setShowCustomColor(true);
                    } else {
                      setShowCustomColor(false);
                      set('materialColor', e.target.value);
                    }
                  }}
                >
                  <option value="" disabled style={{ display: 'none' }}>
                    {`Custom (${loc.materialColor})`}
                  </option>
                  {MATERIAL_COLORS.map(c => (
                    <option key={c.hex} value={c.hex}>{c.name}</option>
                  ))}
                  <option value="__custom__">Custom…</option>
                </select>
              </div>
              {showCustomColor && (
                <div className="param-custom-color-row">
                  <input
                    type="color"
                    value={customHex}
                    onChange={e => {
                      setCustomHex(e.target.value);
                      set('materialColor', e.target.value);
                    }}
                    style={{ width: 36, height: 28, padding: 1, cursor: 'pointer', border: '1px solid #888', borderRadius: 3 }}
                  />
                  <input
                    type="text"
                    value={customHex}
                    maxLength={7}
                    onChange={e => {
                      const v = e.target.value;
                      setCustomHex(v);
                      if (/^#[0-9a-fA-F]{6}$/.test(v)) set('materialColor', v);
                    }}
                    style={{ width: 72, fontFamily: 'monospace', padding: '2px 4px', border: '1px solid #888', borderRadius: 3 }}
                  />
                </div>
              )}
            </div>

            <NumField label="Transparency [0-100]:" value={loc.transparency}
              min={0} step={1} onChange={v => set('transparency', Math.min(100, v))} />

            <label className="param-check">
              <input type="checkbox" checked={loc.materialAboveImage}
                onChange={e => set('materialAboveImage', e.target.checked)} />
              {' '}Material above the image
            </label>
            <label className="param-check">
              <input type="checkbox" checked={loc.multipleContours}
                onChange={e => set('multipleContours', e.target.checked)} />
              {' '}Multiple contours
            </label>
          </div>

          {/* ── Kolumna 2: opcje, przyciski ── */}
          <div className="param-col">
            <div className="param-section">Options:</div>

            <select className="param-select-full" value={loc.pictureQuality}
              onChange={e => set('pictureQuality', e.target.value as PictureQuality)}>
              {(Object.keys(QUALITY_LABELS) as PictureQuality[]).map(k => (
                <option key={k} value={k}>{QUALITY_LABELS[k]}</option>
              ))}
            </select>

            <select className="param-select-full" value={loc.gridDensity}
              onChange={e => set('gridDensity', e.target.value as GridDensity)}>
              {(Object.keys(DENSITY_LABELS) as GridDensity[]).map(k => (
                <option key={k} value={k}>{DENSITY_LABELS[k]}</option>
              ))}
            </select>

            <select className="param-select-full" value={loc.machineType}
              onChange={e => {
                const newId = parseInt(e.target.value, 10);
                const newMachine = MACHINE_TYPES.find(m => m.id === newId) ?? MACHINE_TYPES[0];
                setLoc(prev => ({
                  ...prev,
                  machineType: newId,
                  xSizeMm: Math.min(prev.xSizeMm, newMachine.widthMm),
                  ySizeMm: Math.min(prev.ySizeMm, newMachine.heightMm),
                }));
              }}>
              {MACHINE_TYPES.map(m => (
                <option key={m.id} value={m.id}>
                  Machine type {m.id} ({m.widthMm} x {m.heightMm})
                </option>
              ))}
            </select>

            <select className="param-select-full" value={loc.previewTexturePx}
              onChange={e => set('previewTexturePx', parseInt(e.target.value, 10) as PreviewTexturePx)}>
              {PREVIEW_TEX_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <div className="param-gap" />
            <button className="param-btn-advanced"
              onClick={() => setShowAdvanced(true)}>
              Advanced (FEM Solver)…
            </button>

            <div className="param-gap" />
            <button onClick={onClose}>Cancel</button>
            <div className="param-gap" />
            <button onClick={handleSave}>Save and close</button>
          </div>

        </div>
      </div>
    </div>

    {showAdvanced && (
      <FemAdvancedDialog
        values={femParams}
        onSave={setFemParams}
        onClose={() => setShowAdvanced(false)}
      />
    )}
  </>
  );
};

export default ParametersDialog;
